import React from 'react'
import {Link} from 'react-router-dom'
import {ProjectGuestProvider, useProjectGuest} from './ProjectGuestContext'

// Split out so it sits inside ProjectGuestProvider and can read the signed-in guest from
// context — ProjectGuestLayout itself renders the provider, so it cannot consume it.
function ProjectGuestHeader() {
  const {profile} = useProjectGuest()

  const name = profile ? `${profile.name || ''} ${profile.surname || ''}`.trim() : ''

  return (
    // Stacked and centred on the narrowest screens, name-left/button-right from sm up. The name
    // and email stay left-aligned beside the avatar at every size.
    <div className='d-flex flex-column flex-sm-row align-items-center justify-content-center justify-content-sm-between gap-3 px-5 px-lg-10 py-4'>
      <div className='d-flex align-items-center gap-3 text-start'>
        {name && (
          <div
            className='d-flex flex-center rounded-circle fw-bold text-white'
            style={{width: 36, height: 36, backgroundColor: '#00838f', flexShrink: 0}}
            aria-hidden='true'
          >
            {name.charAt(0).toUpperCase()}
          </div>
        )}
        <div>
          {name && <div className='fw-bold text-dark lh-1'>{name}</div>}
          {profile?.email && <div className='text-gray-600 fs-8'>{profile.email}</div>}
        </div>
      </div>

      {/* Must be a logout route — that renders Logout.tsx, which is what actually signs the
          session out (POST /connect/log-out, dispatch logout(), clear localStorage). Linking
          straight to the login page instead would skip the sign-out entirely and just bounce
          back to the portal home, since the guest is still authenticated.

          The guest-specific /projects/guest/logout rather than plain /logout: it is what tells
          Logout to finish on the portal login. Reaching it by the shared /logout leaves Logout
          inferring the guest from session state that has often already been torn down by then,
          landing the guest on the CRM login instead. */}
      <Link
        to='/projects/guest/logout'
        className='btn btn-light border border-gray-300 d-flex align-items-center gap-2 px-5 py-3'
      >
        <i className='fa-duotone fa-solid fa-right-from-bracket' />
        <span>Log out</span>
      </Link>
    </div>
  )
}

// Temporary minimal shell — only the signed-in guest and "Log out" are shown for now; the
// project switcher, nav bar, and profile link are hidden until the rest of the portal is ready
// to surface. Deliberately does NOT reuse MasterLayout: that pulls in
// PermissionsClient.ForCurrentUser()/AccountSettingsClient.GetFeatures() on mount, surface a
// project guest has no use for and shouldn't be able to reach (Phase 4.3).
export function ProjectGuestLayout({children}: {children: React.ReactNode}) {
  return (
    <ProjectGuestProvider>
      <div className='d-flex flex-column' style={{minHeight: '100vh', backgroundColor: '#f5f8fa'}}>
        <ProjectGuestHeader />
        <div className='container-xxl py-8 flex-column-fluid'>{children}</div>
      </div>
    </ProjectGuestProvider>
  )
}
